import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Save, FileCheck, Clock, CheckCircle2, Lock,
  TrendingUp, TrendingDown, Plus,
} from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  draft: 'bg-amber-100 text-amber-800 border-amber-200',
  submitted: 'bg-blue-100 text-blue-800 border-blue-200',
  reviewed: 'bg-purple-100 text-purple-800 border-purple-200',
  closed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

export default function PerformanceReview() {
  const user = useCurrentUser();
  const isReviewer = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'service_manager' || user?.role === 'floor_manager';
  const [tab, setTab] = useState(isReviewer && user?.role !== 'mechanic' ? 'review' : 'mine');

  return (
    <Layout user={user}>
      <div className="space-y-6 max-w-6xl" data-testid="performance-review-page">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Performance Review</h1>
            <p className="text-sm text-slate-500">Quarterly self-review with productivity insights</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`grid ${isReviewer ? 'grid-cols-2 max-w-md' : 'grid-cols-1 max-w-xs'}`} data-testid="pr-tabs">
            <TabsTrigger value="mine" data-testid="pr-tab-mine">My Reviews</TabsTrigger>
            {isReviewer && <TabsTrigger value="review" data-testid="pr-tab-review">Review Team</TabsTrigger>}
          </TabsList>
          <TabsContent value="mine" className="mt-6"><MyReviewsTab /></TabsContent>
          {isReviewer && <TabsContent value="review" className="mt-6"><AdminReviewsTab role={user?.role} /></TabsContent>}
        </Tabs>
      </div>
    </Layout>
  );
}

function MyReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showStart, setShowStart] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/my`, { credentials: 'include' });
      if (res.ok) setReviews((await res.json()).reviews || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (editingId) {
    return <ReviewEditor reviewId={editingId} onClose={() => { setEditingId(null); fetchData(); }} canEdit={true} canSACommment={false} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{reviews.length} review(s)</p>
        <Button onClick={() => setShowStart(true)} data-testid="start-review-btn">
          <Plus className="h-4 w-4 mr-1" /> Start a review
        </Button>
      </div>
      {loading ? <Card className="p-6">Loading…</Card>
        : reviews.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500">No reviews yet. Start your first quarterly review.</Card>
        : reviews.map(r => (
          <Card key={r.review_id} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setEditingId(r.review_id)} data-testid={`my-review-${r.review_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-bold text-lg">{r.label}</p>
                <p className="text-xs text-slate-500">
                  Productivity: {r.matrix?.performance_pct ?? 0}% · {r.matrix?.total_actual_hours ?? 0}h of {r.matrix?.total_expected_hours ?? 0}h
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${STATUS_BADGE[r.status]}`}>
                {r.status}
              </span>
            </div>
            {r.overall_super_admin_comment && (
              <p className="text-xs text-purple-700 mt-2 italic">💬 Manager: "{r.overall_super_admin_comment.slice(0, 200)}{r.overall_super_admin_comment.length > 200 ? '…' : ''}"</p>
            )}
          </Card>
        ))}
      <StartReviewDialog open={showStart} onClose={() => setShowStart(false)} onStarted={(id) => { setShowStart(false); fetchData(); setEditingId(id); }} />
    </div>
  );
}

function StartReviewDialog({ open, onClose, onStarted }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [saving, setSaving] = useState(false);

  const handleStart = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/start`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, quarter }),
      });
      if (res.ok) {
        const j = await res.json();
        toast.success(j.resumed ? 'Resumed existing review' : 'Review started');
        onStarted(j.review.review_id);
      } else toast.error('Failed to start');
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start / Resume a Quarterly Review</DialogTitle>
          <DialogDescription>The system will load your productivity matrix automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Year</label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())} data-testid="start-review-year" />
          </div>
          <div>
            <label className="text-sm font-medium">Quarter</label>
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(parseInt(v))}>
              <SelectTrigger data-testid="start-review-quarter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map(q => <SelectItem key={q} value={String(q)}>Q{q} ({['Jan-Mar','Apr-Jun','Jul-Sep','Oct-Dec'][q-1]})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleStart} disabled={saving} data-testid="confirm-start-review">{saving ? 'Starting…' : 'Start'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewEditor({ reviewId, onClose, canEdit, canSACommment }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [evidence, setEvidence] = useState({});
  const [saComments, setSAComments] = useState({});
  const [overall, setOverall] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchReview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setReview(d);
        const a = {}; const e = {}; const sa = {};
        (d.questions || []).forEach(q => {
          a[q.id] = q.answer || '';
          e[q.id] = q.evidence_files || [];
          sa[q.id] = q.super_admin_comment || '';
        });
        setAnswers(a); setEvidence(e); setSAComments(sa);
        setOverall(d.overall_super_admin_comment || '');
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [reviewId]);

  useEffect(() => { fetchReview(); }, [fetchReview]);

  if (loading || !review) return <Card className="p-6">Loading…</Card>;
  const isMine = canEdit && review.status === 'draft';

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, evidence_files: evidence }),
      });
      if (res.ok) toast.success('Saved'); else toast.error('Failed to save');
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!confirm('Submit your review? You will not be able to edit after this.')) return;
    setSaving(true);
    try {
      // Save first
      await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, evidence_files: evidence }),
      });
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}/submit`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Submitted to your manager'); onClose(); }
      else { const e = await res.json().catch(()=>({})); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleSACommentSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}/sa-comment`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: saComments, overall }),
      });
      if (res.ok) { toast.success('Comments saved · status: reviewed'); fetchReview(); }
      else toast.error('Failed');
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleClose = async () => {
    if (!confirm('Close this review? Mechanic and you can still view it.')) return;
    const res = await fetch(`${BACKEND_URL}/api/performance-reviews/${reviewId}/close`, { method: 'POST', credentials: 'include' });
    if (res.ok) { toast.success('Closed'); onClose(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onClose} data-testid="back-to-list-btn">← Back</Button>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${STATUS_BADGE[review.status]}`}>{review.status}</span>
          <span className="text-sm font-semibold">{review.employee_name} · {review.label}</span>
        </div>
      </div>

      {/* Productivity matrix summary */}
      <Card className="p-4 bg-slate-50 border-l-4 border-l-indigo-500">
        <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
          {review.matrix?.performance_pct >= 100 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}
          Productivity Matrix · {review.label}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <Mini label="Expected" value={`${review.matrix?.total_expected_hours ?? 0}h`} />
          <Mini label="Actual" value={`${review.matrix?.total_actual_hours ?? 0}h`} />
          <Mini label="Deficit" value={`${review.matrix?.total_deficit_hours ?? 0}h`}
            color={review.matrix?.total_deficit_hours > 0 ? 'rose' : 'emerald'} />
          <Mini label="Performance" value={`${review.matrix?.performance_pct ?? 0}%`}
            color={review.matrix?.performance_pct >= 100 ? 'emerald' : review.matrix?.performance_pct >= 85 ? 'amber' : 'rose'} />
          <Mini label="Months" value={`${review.matrix?.months?.length ?? 0}`} />
        </div>
      </Card>

      {/* Questions */}
      {(review.questions || []).map(q => (
        <Card key={q.id} className="p-4" data-testid={`review-q-${q.id}`}>
          <p className="font-semibold text-slate-900 mb-1">{q.title}</p>
          <p className="text-xs text-slate-600 mb-3">{q.prompt}</p>
          <Textarea
            value={answers[q.id] || ''}
            onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
            disabled={!isMine}
            rows={4}
            placeholder={isMine ? 'Type your answer here…' : '(no answer provided)'}
            data-testid={`review-answer-${q.id}`}
          />
          {/* Evidence */}
          <div className="mt-2">
            <label className="text-xs text-slate-600">Evidence files (URLs, optional):</label>
            <Input
              value={(evidence[q.id] || []).join(', ')}
              onChange={(e) => setEvidence({ ...evidence, [q.id]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              disabled={!isMine}
              placeholder="paste links comma-separated (photos/certificates)"
              data-testid={`review-evidence-${q.id}`}
            />
          </div>
          {/* SA comments */}
          {(canSACommment || q.super_admin_comment) && (
            <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
              <p className="text-[10px] uppercase tracking-wider text-purple-700 font-bold mb-1">Manager Comment</p>
              <Textarea
                value={saComments[q.id] || ''}
                onChange={(e) => setSAComments({ ...saComments, [q.id]: e.target.value })}
                disabled={!canSACommment}
                rows={2}
                placeholder={canSACommment ? 'Your feedback for this answer…' : '(no comment)'}
                data-testid={`sa-comment-${q.id}`}
              />
            </div>
          )}
        </Card>
      ))}

      {/* Overall comment for SA */}
      {(canSACommment || review.overall_super_admin_comment) && (
        <Card className="p-4 bg-indigo-50 border-l-4 border-l-indigo-500">
          <p className="text-sm font-bold text-indigo-900 mb-1">Overall Manager Comment</p>
          <Textarea
            value={overall}
            onChange={(e) => setOverall(e.target.value)}
            disabled={!canSACommment}
            rows={3}
            placeholder={canSACommment ? 'Closing remarks for the mechanic…' : ''}
            data-testid="overall-comment"
          />
        </Card>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        {isMine && (
          <>
            <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-draft-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="submit-review-btn">
              <Send className="h-4 w-4 mr-1" /> Submit to Manager
            </Button>
          </>
        )}
        {canSACommment && review.status === 'submitted' && (
          <Button onClick={handleSACommentSave} disabled={saving} data-testid="save-sa-comments-btn">
            <FileCheck className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save Comments'}
          </Button>
        )}
        {canSACommment && review.status === 'reviewed' && (
          <>
            <Button variant="outline" onClick={handleSACommentSave} disabled={saving}>Update Comments</Button>
            <Button onClick={handleClose} data-testid="close-review-btn">
              <Lock className="h-4 w-4 mr-1" /> Close Review
            </Button>
          </>
        )}
        {!isMine && review.status === 'draft' && (
          <p className="text-xs text-slate-500 italic">Mechanic still editing — no manager action yet.</p>
        )}
        {review.status === 'closed' && (
          <p className="text-xs text-emerald-700 italic flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Review closed.</p>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, color }) {
  const c = { rose: 'text-rose-700 bg-rose-50', emerald: 'text-emerald-700 bg-emerald-50', amber: 'text-amber-700 bg-amber-50' }[color] || 'text-slate-700 bg-white';
  return (
    <div className={`px-2 py-1 rounded ${c}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="font-bold">{value}</p>
    </div>
  );
}

function AdminReviewsTab({ role }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const url = filterStatus === 'all' ? `${BACKEND_URL}/api/performance-reviews/all` : `${BACKEND_URL}/api/performance-reviews/all?status=${filterStatus}`;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setReviews((await res.json()).reviews || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (editingId) {
    return <ReviewEditor reviewId={editingId} onClose={() => { setEditingId(null); fetchData(); }} canEdit={false} canSACommment={role === 'super_admin' || role === 'admin'} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-600">{reviews.length} review(s)</p>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted (action needed)</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <Card className="p-6">Loading…</Card>
        : reviews.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500">No reviews match.</Card>
        : reviews.map(r => (
          <Card key={r.review_id} className="p-4 hover:shadow-md cursor-pointer" onClick={() => setEditingId(r.review_id)} data-testid={`admin-review-${r.review_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-bold">{r.employee_name} · {r.label}</p>
                <p className="text-xs text-slate-500">
                  Productivity: {r.matrix?.performance_pct ?? 0}% · {r.matrix?.total_actual_hours ?? 0}h of {r.matrix?.total_expected_hours ?? 0}h
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${STATUS_BADGE[r.status]}`}>{r.status}</span>
            </div>
          </Card>
        ))}
    </div>
  );
}
